import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import { InfoMessage } from "@/components/ui/InfoMessage";
import { Mountain, X } from "lucide-react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import CloseIcon from "@/components/icons/CloseIcon";
import EditIcon from "@/components/icons/EditIcon";
import ViewIcon from "@/components/icons/ViewIcon";

const MineTypeModal = ({ isOpen, onClose, mineType, mode }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [viewFileUrl, setViewFileUrl] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Disable background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (mode === "edit" && mineType) {
      setFormData({
        name: mineType.name || "",
        description: mineType.description || "",
        is_active: mineType.is_active ?? true,
        usage_count: mineType.usage_count,
        reports_count: mineType.reports_count,
        site_names: mineType.site_names,
      });
      setUploadedFile(null);

      // Set existing file URL if file exists and status is active
      if (mineType.file_path && mineType.is_active) {
        // Use default values if bucket info is missing
        const bucketName = mineType.bucket_name || "minesure";
        const regionName = mineType.region_name || "eu-north-1";

        const url = `https://${bucketName}.s3.${regionName}.amazonaws.com/${mineType.file_path}`;
        setViewFileUrl(url);
      } else {
        setViewFileUrl(null);
      }
    } else {
      setFormData({
        name: "",
        description: "",
        is_active: true,
      });
      setUploadedFile(null);
      setViewFileUrl(null);
    }
  }, [mode, mineType, isOpen]);

  const createMutation = useMutation((data) => api.post("/mine-type", data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["mine-types"]);
      toast.success("Mine type created successfully");
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create mine type");
    },
  });

  const updateMutation = useMutation((data) => api.put(`/mine-type/${mineType.id}`, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["mine-types"]);
      toast.success("Mine type updated successfully");
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update mine type");
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If status changes to inactive, clear any uploaded files and show confirmation
    if (name === "is_active" && value === false) {
      // Clear all file-related state
      setUploadedFile(null);
      setViewFileUrl(null);
      setIsDragging(false);

      // Show user feedback about file removal
      if (viewFileUrl || uploadedFile) {
        toast.success("File will be removed when you save the changes");
      }
    }

    // If status changes to active, we might need to restore file state
    if (name === "is_active" && value === true && mode === "edit" && mineType?.file_path) {
      // Restore existing file URL when switching back to active
      const bucketName = mineType.bucket_name || "minesure";
      const regionName = mineType.region_name || "eu-north-1";
      const url = `https://${bucketName}.s3.${regionName}.amazonaws.com/${mineType.file_path}`;
      setViewFileUrl(url);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Mine type name is required");
      return;
    }

    // File validation logic - only required when status is Active
    if (formData.is_active) {
      const hasExistingFile = mode === "edit" && viewFileUrl;
      const hasNewFile = uploadedFile;
      const hasAnyFile = hasExistingFile || hasNewFile;

      // File is required for create mode when Active
      if (mode === "create" && !hasNewFile) {
        toast.error("Skeleton report is required when status is active");
        return;
      }

      // File is required for edit mode when Active (either existing or new)
      if (mode === "edit" && !hasAnyFile) {
        toast.error("Skeleton report is required when status is active");
        return;
      }
    }

    const dataToSend = new FormData();
    dataToSend.append("name", formData.name);
    dataToSend.append("description", formData.description || "");
    dataToSend.append("is_active", formData.is_active);

    if (formData.is_active && uploadedFile) {
      dataToSend.append("skeleton_report", uploadedFile);
    }

    if (!formData.is_active) {
      dataToSend.append("remove_file", "true");
      if (viewFileUrl) {
        setViewFileUrl(null);
      }
      if (uploadedFile) {
        setUploadedFile(null);
      }
    }

    if (mode === "create") {
      createMutation.mutate(dataToSend);
    } else {
      updateMutation.mutate(dataToSend);
    }
  };

  const handleFiles = (files) => {
    if (!formData.is_active) {
      toast.error("No file upload needed when status is Inactive");
      return;
    }

    // Check if a file already exists
    if (viewFileUrl || uploadedFile) {
      toast.error("A file is already selected. Please remove it first to upload a different file.");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const maxFileSize = 25 * 1024 * 1024; // 25MB in bytes

    const validFiles = files.filter((file) => {
      // Check file type
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Only PDF and Word documents are allowed.`);
        return false;
      }

      // Check file size
      if (file.size > maxFileSize) {
        toast.error(`File too large. Maximum size is 25MB.`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) {
      return;
    }

    setUploadedFile(validFiles[0]);
    toast.success("File uploaded successfully!");
  };


  const handleViewUploadedFile = (file) => {
    if (!file) {
      toast.error("No file to view");
      return;
    }

    const fileUrl = URL.createObjectURL(file);
    const newWindow = window.open(fileUrl, "_blank");

    if (!newWindow) {
      toast.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    setTimeout(() => {
      URL.revokeObjectURL(fileUrl);
    }, 5000);
  };

  // Handle view existing file
  const handleViewExistingFile = async () => {
    try {
      const response = await api.get(`/mine-type/${mineType.id}/download?action=view`);

      const fileUrl = response.data.url;

      if (!fileUrl) {
        toast.error("No file URL returned", { id: "view-file" });
        return;
      }

      const newWindow = window.open(fileUrl, "_blank");

      if (!newWindow) {
        toast.error("Popup blocked. Please allow popups for this site.", { id: "view-file" });
        return;
      }

      toast.success("File opened successfully", { id: "view-file" });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load file", { id: "view-file" });
    }
  };
  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div
      className={`fixed inset-0 z-50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
    >
      <div
        className={`fixed inset-0 bg-white bg-opacity-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      ></div>
      <div className="fixed inset-0 flex items-center justify-center py-8 px-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <div
          className={`w-full max-w-xl transform rounded-lg bg-navy-500 transition-all duration-300 ${isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"
            } max-h-[calc(100vh-4rem)] my-auto flex flex-col sm:w-11/12 md:w-9/12 lg:w-8/12`}
        >
          <DialogHeader className="sticky top-0 z-10 px-6 pt-6 pb-4 rounded-t-lg bg-navy-500">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-0 sm:text-xl md:text-2xl">
              {mode === "create" ? "Create Mine Type" : "Edit Mine Type"}
              <CloseIcon
                className="absolute w-8 h-8 text-red-500 transition-transform duration-200 cursor-pointer top-6 right-6 hover:scale-110"
                onClick={onClose}
              />
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 pt-4 mb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div>
                {formData.usage_count && formData.usage_count != 0 && (
                  <InfoMessage>
                    Cannot delete this mine type while it is linked to sites.
                  </InfoMessage>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter mine type name"
                    className="border-primary/20 focus:border-primary/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description (optional)"
                    rows={3}
                    className="border-primary/20 focus:border-primary/40"
                  />
                </div>

                <div className="w-full space-y-2 sm:w-auto">
                  <Label>Status</Label>
                  <Select
                    value={formData.is_active.toString()}
                    onValueChange={(value) => handleSelectChange("is_active", value === "true")}
                  >
                    <SelectTrigger className="w-full rounded-t-lg border-primary/20 focus:border-primary/40">
                      <SelectValue placeholder="Select Status">
                        {formData.is_active ? "Active" : "Inactive"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="z-50 text-gray-400 shadow-lg bg-navy-100">
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Skeleton Report Section - Only show when status is Active */}
                {formData.is_active && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase text-gray-0">
                      Skeleton report *
                    </Label>

                    {/* File Upload Area - Only show if no file exists */}
                    {!viewFileUrl && !uploadedFile && (
                      <div
                        className={`rounded-lg border-2 border-dashed bg-navy-100 p-6 text-center transition-all duration-300 ${isDragging ? "border-red-500 shadow-lg" : "border-gray-600"
                          }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(false);
                          const files = Array.from(e.dataTransfer.files);
                          if (files.length > 0) {
                            handleFiles(files);
                          }
                        }}
                      >
                        <div className="text-gray-0">
                          <p className="text-sm uppercase sm:text-base">
                            {isDragging ? "Drop files here" : "Drag and drop files here"}
                          </p>
                          <p className="mt-2 text-xs sm:text-sm">or</p>
                          <input
                            type="file"
                            className="hidden"
                            id="fileInput"
                            accept=".pdf,.doc,.docx"
                            disabled={viewFileUrl || uploadedFile}
                            onChange={(e) => {
                              if (viewFileUrl || uploadedFile) {
                                toast.error(
                                  "A file is already selected. Please remove it first to upload a different file."
                                );
                                return;
                              }
                              const files = Array.from(e.target.files);
                              if (files.length > 0) {
                                handleFiles(files);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            onClick={() => {
                              if (viewFileUrl || uploadedFile) {
                                toast.error(
                                  "A file is already selected. Please remove it first to upload a different file."
                                );
                                return;
                              }
                              document.getElementById("fileInput").click();
                            }}
                            variant="outline"
                            disabled={viewFileUrl || uploadedFile}
                            className="w-full mt-3 sm:mt-0 sm:w-auto"
                          >
                            Browse Files
                          </Button>
                          <p className="mt-2 text-xs leading-snug text-gray-400">
                            Maximum file size: 25MB. Supported formats: PDF, DOC, DOCX
                            {mode === "edit" && <br />}
                            {mode === "edit" && "Leave empty to keep existing file"}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Message when file already exists */}
                    {(viewFileUrl || uploadedFile) && (
                      <div className="text-sm text-gray-100 sm:text-base">
                        A skeleton report file is already selected. Remove the current file to upload a
                        different one.
                      </div>
                    )}

                    {/* Display Existing File (Edit Mode) */}
                    {mode === "edit" && viewFileUrl && !uploadedFile && (
                      <div className="p-2 border border-gray-600 rounded-lg bg-navy-100">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-col">
                            <span className="text-gray-0">Current File</span>
                            <span className="text-xs text-gray-400">
                              {mineType?.file_path?.split("/").pop() || "Existing file"}
                            </span>
                          </div>
                          <div className="flex flex-col w-full gap-2 sm:w-auto sm:flex-row">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleViewExistingFile}
                              className="w-full sm:w-auto"
                            >
                              View
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setViewFileUrl(null);
                                toast.success("Current file removed. You can now upload a new file.");
                              }}
                              className="w-full sm:w-auto"
                            >
                              Replace
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Display Uploaded File */}
                    {uploadedFile && (
                      <div className="p-2 border border-gray-600 rounded-lg bg-navy-100">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-col">
                            <span className="text-gray-0">New File</span>
                            <span className="text-xs text-gray-400 break-all">
                              {uploadedFile.name} ({formatFileSize(uploadedFile.size)})
                            </span>
                          </div>
                          <div className="flex flex-col w-full gap-2 sm:w-auto sm:flex-row">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleViewUploadedFile(uploadedFile)}
                              className="w-full sm:w-auto"
                            >
                              View
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setUploadedFile(null);
                                toast.success("New file removed.");
                              }}
                              className="w-full sm:w-auto"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}



                  </div>
                )}

                {mode === "edit" && (
                  <div className="flex flex-col gap-2 space-y-2">
                    <div>
                      <Label htmlFor="category">Risk Sections</Label>
                    </div>
                    <div className="flex flex-row gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex items-center justify-center w-1/2 gap-2 rounded-t-lg bg-navy-100"
                        onClick={() => {
                          if (mineType?.id) {
                            navigate(`/mine-types/${mineType.id}/category/view`);
                            onClose();
                          }
                        }}
                      >
                        <ViewIcon className="w-4 h-4" />View
                      </Button>
                      {(!mineType?.reports_count || mineType.reports_count === 0 || parseInt(mineType.reports_count) === 0) && (
                        <Button
                          type="button"
                          variant="outline"
                          className="flex items-center justify-center w-1/2 gap-2 rounded-t-lg bg-navy-100"
                          onClick={() => {
                            if (mineType?.id) {
                              navigate(`/mine-types/${mineType.id}/category/edit`);
                              onClose();
                            }
                          }}
                        >
                          <EditIcon className="w-4 h-4" />Edit
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 z-10 px-6 pt-4 pb-6 rounded-b-lg bg-navy-500">
              <div className="flex flex-col gap-3 sm:flex-row">
                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full gradient-primary rounded-xl sm:flex-1"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                      {mode === "create" ? "Creating..." : "Updating..."}
                    </>
                  ) : mode === "create" ? (
                    "Create Mine Type"
                  ) : (
                    "Update Mine Type"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                  className="w-full rounded-xl sm:w-auto"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MineTypeModal;
